import type {
  DisqualificationConfig,
  DisqualificationRule,
  DisqualifiedResultConfig,
} from "@portal/app/_server/services/formFunnel/payloadTypes";

/** Hardcoded fallback when neither rule nor funnel-level result is configured. */
const DEFAULT_RESULT_CONFIG: DisqualifiedResultConfig = {
  title: "We're Sorry",
  message: "Unfortunately, you do not qualify for this program.",
};

/**
 * Result returned when a user is disqualified by a rule.
 */
export interface DisqualificationResult {
  isDisqualified: true;
  triggeredRule: DisqualificationRule;
  resultConfig: DisqualifiedResultConfig;
}

/**
 * Evaluates a single disqualification rule against an answer value.
 */
function evaluateRule(rule: DisqualificationRule, answerValue: unknown): boolean {
  const { operator, values } = rule;

  // No answer provided - rule doesn't trigger
  if (answerValue === undefined || answerValue === null) {
    return false;
  }

  // Handle array answers (e.g. from multiselect questions)
  const answerArray = Array.isArray(answerValue)
    ? answerValue.map(String)
    : null;

  // Convert answer to string for scalar comparison
  const answerStr = answerArray ? "" : String(answerValue);

  switch (operator) {
    case "equals":
    case "in":
      // Both operators check if the answer is in the values array.
      // For array answers, check if ANY selected value matches.
      return answerArray
        ? answerArray.some((v) => values.includes(v))
        : values.includes(answerStr);
    case "not_equals":
      // Disqualify if answer is NOT in the values array.
      // For array answers, disqualify if NONE of the selected values are in the allowed list.
      return answerArray
        ? answerArray.every((v) => !values.includes(v))
        : !values.includes(answerStr);
    case "contains":
      // Disqualify if the answer contains any of the values (case-insensitive).
      // For array answers, check if ANY element contains any of the values.
      return answerArray
        ? answerArray.some((a) =>
            values.some((v) => a.toLowerCase().includes(v.toLowerCase()))
          )
        : values.some((v) => answerStr.toLowerCase().includes(v.toLowerCase()));
    default:
      return false;
  }
}

/**
 * Returns true if the result config has at least one text field set.
 * Payload CMS always creates the group object (even when empty), so we
 * check text fields to distinguish "admin configured a custom result"
 * from "admin created a rule but left the result group untouched".
 */
function hasOverrideFields(result: DisqualifiedResultConfig): boolean {
  return !!(
    result.title ||
    result.message ||
    result.redirectUrl ||
    result.redirectButtonText ||
    result.changeAnswerTargetQuestion
  );
}

/**
 * Merges a rule-level result config with the funnel-level default.
 * Rule-level text fields take precedence; missing fields fall back to the
 * funnel default, then to the hardcoded default. If no text fields are set
 * on the rule result, the default is used as-is (see `hasOverrideFields`).
 */
function resolveResultConfig(
  ruleResult: DisqualifiedResultConfig | undefined,
  defaultResult: DisqualifiedResultConfig | undefined
): DisqualifiedResultConfig {
  const base = defaultResult || DEFAULT_RESULT_CONFIG;

  // No rule-level override, or override has no meaningful fields -- use the default as-is
  if (!ruleResult || !hasOverrideFields(ruleResult)) return base;

  // Boolean fields inferred from related text fields (not in per-rule CMS schema;
  // see migration for rationale). Setting a text field implies showing its button.
  // NOTE: When adding new fields to DisqualifiedResultConfig, add them here too.
  return {
    title: ruleResult.title ?? base.title,
    message: ruleResult.message ?? base.message,
    showChangeAnswerButton: ruleResult.changeAnswerTargetQuestion
      ? true
      : base.showChangeAnswerButton,
    changeAnswerTargetQuestion:
      ruleResult.changeAnswerTargetQuestion ?? base.changeAnswerTargetQuestion,
    showRedirectButton: ruleResult.redirectUrl
      ? true
      : base.showRedirectButton,
    redirectUrl: ruleResult.redirectUrl ?? base.redirectUrl,
    redirectButtonText: ruleResult.redirectButtonText ?? base.redirectButtonText,
  };
}

/**
 * Checks if the user should be disqualified based on their most recent answer.
 *
 * This function is called after each question is answered. It only evaluates
 * rules that apply to the question that was just answered (based on questionKey).
 *
 * Each rule can optionally define its own result configuration. If a rule does
 * not define a result (or leaves fields empty), the funnel-level default result
 * is used as a fallback.
 *
 * @param answers - All form answers collected so far
 * @param lastAnsweredQuestionKey - The fieldKey of the question that was just answered
 * @param config - The disqualification configuration from the funnel
 * @returns DisqualificationResult if disqualified, null otherwise
 */
export function checkDisqualification(
  answers: Record<string, unknown>,
  lastAnsweredQuestionKey: string,
  config?: DisqualificationConfig
): DisqualificationResult | null {
  // Early return if disqualification is not configured or enabled
  if (!config?.enabled || !config.rules?.length) {
    return null;
  }

  // Only check rules that apply to the just-answered question
  const applicableRules = config.rules.filter(
    (rule) => rule.questionKey === lastAnsweredQuestionKey
  );

  // Evaluate each applicable rule
  for (const rule of applicableRules) {
    const answerValue = answers[rule.questionKey];

    if (evaluateRule(rule, answerValue)) {
      return {
        isDisqualified: true,
        triggeredRule: rule,
        resultConfig: resolveResultConfig(rule.result, config.result),
      };
    }
  }

  return null;
}
